import { supabase, handleSupabaseError } from './config';
import { createClient } from '@supabase/supabase-js';
import { getCategoryDiscountQuota } from '../utils/categoryQuota';

// Create a separate admin client using service key for bypassing RLS
const adminSupabase = process.env.REACT_APP_SUPABASE_SERVICE_KEY ? createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_SERVICE_KEY,
  { 
    auth: { persistSession: false },
    db: { schema: 'public' }
  }
) : null;

// Utility: detect missing-column errors returned by PostgREST
const isMissingColumnError = (err) => {
  if (!err) return false;
  const msg = String(err.message || '').toLowerCase();
  return err.code === '42703' || msg.includes('column') && (msg.includes('does not exist') || msg.includes('could not find')) || msg.includes('schema cache');
};

// --- FUNGSI UNTUK PENGGUNA (USERS) ---

/**
 * Membuat profil pengguna baru di Supabase setelah registrasi.
 * @param {object} userAuth - Objek pengguna dari Supabase Authentication.
 * @param {object} additionalData - Data tambahan seperti peran, nama, dan kode mitra.
 */
export const createUserProfileDocument = async (userAuth, additionalData) => {
  if (!userAuth) return;

  try {
    const userData = {
      id: userAuth.id,
      email: userAuth.email,
      created_at: new Date().toISOString(),
      ...additionalData,
    };

    const { data, error } = await supabase
      .from('app_users')
      .upsert(userData, { onConflict: 'id' })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating user profile:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mengambil data profil seorang pengguna dari Supabase.
 * @param {string} userId - ID pengguna.
 * @returns {object|null} Data profil pengguna atau null jika tidak ditemukan.
 */
export const getUserProfile = async (userId) => {
  if (!userId) return null;
  
  try {
    // Try with admin client first if available (development mode)
    const client = (process.env.REACT_APP_DEV_MODE === 'true' && adminSupabase) ? adminSupabase : supabase;
    
    console.log('🔍 getUserProfile: Using', client === adminSupabase ? 'admin' : 'regular', 'client for userId:', userId);
    
    const { data, error } = await client
      .from('app_users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.warn('⚠️ getUserProfile: Error with first attempt:', error);
      
      // If using regular client failed, try admin client if available
      if (client !== adminSupabase && adminSupabase) {
        console.log('🔄 getUserProfile: Retrying with admin client');
        const { data: adminData, error: adminError } = await adminSupabase
          .from('app_users')
          .select('*')
          .eq('id', userId)
          .single();
          
        if (!adminError) {
          console.log('✅ getUserProfile: Success with admin client');
          return adminData;
        }
        console.warn('⚠️ getUserProfile: Admin client also failed:', adminError);
      }
      
      // If admin client not available or failed, try to get user by email from current session
      console.log('🔄 getUserProfile: Trying email-based lookup');
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        const { data: altData, error: altError } = await client
          .from('app_users')
          .select('*')
          .eq('email', session.user.email)
          .single();
        
        if (!altError) {
          console.log('✅ getUserProfile: Success with email lookup');
          return altData;
        }
        console.warn('⚠️ getUserProfile: Email lookup failed:', altError);
      }
      
      throw error;
    }
    
    if (data) {
      console.log('✅ getUserProfile: Success on first attempt');
    }
    return data;
  } catch (error) {
    console.error('❌ getUserProfile: Final error:', error);
    return null;
  }
};

/**
 * Mengambil semua pengguna dengan peran 'mitra'.
 * @returns {Array} Daftar semua mitra.
 */
export const getAllMitras = async () => {
  try {
    // Use admin client for development mode
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('app_users')
      .select('*')
      .eq('role', 'mitra')
      .order('nama', { ascending: true });

    if (error) {
      console.error('🚨 getAllMitras error:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('❌ Error getting mitras:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Mengupdate data profil pengguna.
 * @param {string} userId - ID pengguna.
 * @param {object} updateData - Data yang akan diupdate.
 */
export const updateUserProfile = async (userId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('app_users')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    handleSupabaseError(error);
  }
};

/**
 * Menghapus dokumen pengguna.
 * @param {string} userId - ID pengguna yang akan dihapus.
 */
export const deleteUser = async (userId) => {
  try {
    const { error } = await supabase
      .from('app_users')
      .delete()
      .eq('id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting user:', error);
    handleSupabaseError(error);
  }
};

// --- FUNGSI UNTUK PENJUALAN (SALES) ---

/**
 * Generate nomor pesanan dari tanggal tertentu
 */
export const generateOrderNumberFromDate = (date) => {
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hour = date.getHours().toString().padStart(2, '0');
  const minute = date.getMinutes().toString().padStart(2, '0');
  const second = date.getSeconds().toString().padStart(2, '0');
  
  return `NH-${year}${month}${day}-${hour}${minute}${second}`;
};

/**
 * Generate nomor pesanan untuk saat ini
 */
export const generateOrderNumber = () => {
  return generateOrderNumberFromDate(new Date());
};

/**
 * Menambahkan dokumen penjualan baru dengan nomor pesanan otomatis.
 * @param {object} saleData - Data lengkap penjualan.
 */
export const addSale = async (saleData) => {
  try {
    const orderNumber = generateOrderNumber();
    const saleId = crypto.randomUUID();

    // Use admin client in development mode to bypass RLS issues
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? (adminSupabase || supabase) : supabase;

    // Whitelist fields to match Supabase schema (snake_case)
    const insertPayload = {
      id: saleId,
      no_pesanan: orderNumber,
      mitra_uid: saleData.mitraUid,
      customer_id: saleData.customerId || null, // Link to customers table
      status: saleData.status || 'menunggu validasi',
      pembayaran: saleData.pembayaran || 'belum dibayar',
      total_harga: saleData.totalHarga,
      harga_setelah_diskon: saleData.hargaSetelahDiskon,
      ongkir: saleData.ongkir,
      subtotal: saleData.subtotal,
      notes: saleData.notes,
      nama_sekolah: saleData.pelanggan?.nama_pelanggan || saleData.namaSekolah || null,
      created_at: new Date().toISOString()
    };

    // Remove undefined keys so we don't send unknown/null columns
    Object.keys(insertPayload).forEach(k => insertPayload[k] === undefined && delete insertPayload[k]);

    // Insert sale record
    const { data: sale, error: saleError } = await client
      .from('sales')
      .insert(insertPayload)
      .select()
      .single();

    if (saleError) throw saleError;

    // Insert sale items if provided
    if (saleData.items && saleData.items.length > 0) {
      const saleItems = saleData.items.map((item, index) => {
        // Validasi kuota diskon berdasarkan kategori
        const categoryQuota = getCategoryDiscountQuota(item.kategori);
        const validDiscount = Math.min(item.diskon_per_item ?? item.diskonPerItem ?? 0, categoryQuota);
        
        return {
          id: `${saleId}-${index}`,
          sale_id: saleId,
          kode_buku: item.kode_buku,
          judul_buku: item.judul_buku,
          harga_buku: item.harga_buku || 0,
          kuantitas: item.kuantitas || 0,
          diskon_per_item: validDiscount
        };
      });

      const { error: itemsError } = await client
        .from('sale_items')
        .insert(saleItems);

      if (itemsError) throw itemsError;
    }

    return { id: saleId, ...sale };
  } catch (error) {
    console.error('Error adding sale:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mengambil semua data penjualan.
 * @returns {Array} Daftar semua penjualan.
 */
export const getAllSales = async () => {
  try {
    // Use admin client for development mode
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('sales')
      .select(`
        *,
        sale_items (*),
        mitra:app_users!mitra_uid (
          nama,
          kode_mitra
        ),
        customer:customers!customer_id (
          nama,
          no_wa,
          alamat
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('🚨 getAllSales error:', error);
      throw error;
    }
    
    
    // Transform data to match Firebase structure
    return (data || []).map(sale => {
      // Map sale items dengan konversi snake_case ke camelCase
      const mappedItems = (sale.sale_items || []).map(item => ({
        ...item,
        diskonPerItem: item.diskon_per_item || 0,
        kodeBuku: item.kode_buku,
        judulBuku: item.judul_buku,
        hargaBuku: item.harga_buku
      }));
      
      return {
        ...sale,
        items: mappedItems,
        // Map field names for compatibility
        noPesanan: sale.no_pesanan,
        namaSekolah: sale.nama_sekolah,
        // Map date fields
        createdAt: sale.created_at,
        updatedAt: sale.updated_at,
        // Include mitra information from joined data
        nama_mitra: sale.mitra?.nama || sale.mitra?.kode_mitra || 'N/A',
        kode_mitra: sale.mitra?.kode_mitra || 'N/A',
      // Include customer information from joined data, JSON field, or legacy fields
      pelanggan: sale.customer ? {
        // From customers table join
        nama_pelanggan: sale.customer.nama,
        no_wa: sale.customer.no_wa,
        alamat: sale.customer.alamat
      } : sale.pelanggan ? {
        // From JSON field stored directly in sales record
        nama_pelanggan: sale.pelanggan.nama_pelanggan,
        no_wa: sale.pelanggan.no_wa,
        alamat: sale.pelanggan.alamat
      } : {
        // Fallback to direct fields or legacy namaSekolah field
        nama_pelanggan: sale.nama_pelanggan || sale.nama_sekolah || sale.namaSekolah || null,
        no_wa: sale.no_wa_pelanggan || sale.no_wa || sale.pelanggan_no_wa || null,
        alamat: sale.alamat_pelanggan || sale.alamat || sale.pelanggan_alamat || sale.alamat_lengkap || null
      }
    };
    });
  } catch (error) {
    console.error('❌ Error getting all sales:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Mengambil data penjualan berdasarkan status.
 * @param {string} status - Status penjualan yang ingin difilter.
 * @returns {Array} Daftar penjualan dengan status tertentu.
 */
export const getSalesByStatus = async (status) => {
  try {
    // Use admin client for development mode
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('sales')
      .select(`
        *,
        sale_items (*),
        mitra:app_users!mitra_uid (
          nama,
          kode_mitra
        ),
        customer:customers!customer_id (
          nama,
          no_wa,
          alamat
        )
      `)
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('🚨 getSalesByStatus error:', error);
      throw error;
    }
    
    
    return (data || []).map(sale => {
      // Map sale items dengan konversi snake_case ke camelCase
      const mappedItems = (sale.sale_items || []).map(item => ({
        ...item,
        diskonPerItem: item.diskon_per_item || 0,
        kodeBuku: item.kode_buku,
        judulBuku: item.judul_buku,
        hargaBuku: item.harga_buku
      }));
      
      return {
        ...sale,
        items: mappedItems,
        // Map field names for compatibility
        noPesanan: sale.no_pesanan,
        namaSekolah: sale.nama_sekolah,
        // Map date fields
        createdAt: sale.created_at,
        updatedAt: sale.updated_at,
        // Include mitra information from joined data
        nama_mitra: sale.mitra?.nama || sale.mitra?.kode_mitra || 'N/A',
        kode_mitra: sale.mitra?.kode_mitra || 'N/A',
      // Include customer information from joined data, JSON field, or legacy fields
      pelanggan: sale.customer ? {
        // From customers table join
        nama_pelanggan: sale.customer.nama,
        no_wa: sale.customer.no_wa,
        alamat: sale.customer.alamat
      } : sale.pelanggan ? {
        // From JSON field stored directly in sales record
        nama_pelanggan: sale.pelanggan.nama_pelanggan,
        no_wa: sale.pelanggan.no_wa,
        alamat: sale.pelanggan.alamat
      } : {
        // Fallback to direct fields or legacy namaSekolah field
        nama_pelanggan: sale.nama_pelanggan || sale.nama_sekolah || sale.namaSekolah || 'N/A',
        no_wa: sale.no_wa_pelanggan || sale.no_wa || sale.pelanggan_no_wa || 'N/A',
        alamat: sale.alamat_pelanggan || sale.alamat || sale.pelanggan_alamat || sale.alamat_lengkap || 'N/A'
      }
    };
    });
  } catch (error) {
    console.error('❌ Error getting sales by status:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Mengambil data penjualan berdasarkan mitra.
 * @param {string} mitraUid - UID mitra.
 * @param {string} statusFilter - Filter status (opsional).
 * @returns {Array} Daftar penjualan dari mitra tertentu.
 */
export const getSalesByMitra = async (mitraUid, statusFilter = null) => {
  try {
    // Use admin client for development mode (same consistency as other functions)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    let query = client
      .from('sales')
      .select(`
        *,
        sale_items (*),
        mitra:app_users!mitra_uid (
          nama,
          kode_mitra
        ),
        customer:customers!customer_id (
          nama,
          no_wa,
          alamat
        )
      `)
      .eq('mitra_uid', mitraUid);

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('🚨 getSalesByMitra error:', error);
      throw error;
    }
    
    // Get all customers for this mitra to match legacy orders by name
    const customersQuery = client
      .from('customers')
      .select('nama, no_wa, alamat')
      .eq('mitra_uid', mitraUid);
    
    const { data: customers, error: customersError } = await customersQuery;
    const customerMap = {};
    
    if (!customersError && customers) {
      customers.forEach(customer => {
        // Create a mapping from customer name to customer data
        if (customer.nama) {
          customerMap[customer.nama.toLowerCase()] = customer;
        }
      });
    }
    
    return (data || []).map(sale => {
      // Try to match legacy orders with existing customers by name
      let matchedCustomer = null;
      if (!sale.customer && sale.nama_sekolah) {
        matchedCustomer = customerMap[sale.nama_sekolah.toLowerCase()];
      }
      
      // Map sale items dengan konversi snake_case ke camelCase
      const mappedItems = (sale.sale_items || []).map(item => ({
        ...item,
        diskonPerItem: item.diskon_per_item || 0,
        kodeBuku: item.kode_buku,
        judulBuku: item.judul_buku,
        hargaBuku: item.harga_buku
      }));
      
      return {
        ...sale,
        items: mappedItems,
        // Map field names for compatibility
        noPesanan: sale.no_pesanan,
        namaSekolah: sale.nama_sekolah,
        // Map date fields
        createdAt: sale.created_at,
        updatedAt: sale.updated_at,
        // Include mitra information from joined data
        nama_mitra: sale.mitra?.nama || sale.mitra?.kode_mitra || 'N/A',
        kode_mitra: sale.mitra?.kode_mitra || 'N/A',
        // Include customer information from joined data, JSON field, matched customer, or legacy fields
        pelanggan: sale.customer ? {
          // From customers table join
          nama_pelanggan: sale.customer.nama,
          no_wa: sale.customer.no_wa,
          alamat: sale.customer.alamat
        } : sale.pelanggan ? {
          // From JSON field stored directly in sales record
          nama_pelanggan: sale.pelanggan.nama_pelanggan,
          no_wa: sale.pelanggan.no_wa,
          alamat: sale.pelanggan.alamat
        } : matchedCustomer ? {
          // From matched customer by name
          nama_pelanggan: matchedCustomer.nama,
          no_wa: matchedCustomer.no_wa,
          alamat: matchedCustomer.alamat
        } : {
          // Fallback to direct fields or legacy namaSekolah field
          nama_pelanggan: sale.nama_pelanggan || sale.nama_sekolah || sale.namaSekolah || null,
          no_wa: sale.no_wa_pelanggan || sale.no_wa || sale.pelanggan_no_wa || null,
          alamat: sale.alamat_pelanggan || sale.alamat || sale.pelanggan_alamat || sale.alamat_lengkap || null
        }
      };
    });
  } catch (error) {
    console.error('Error getting sales by mitra:', error);
    return [];
  }
};

/**
 * Mengupdate status dokumen penjualan.
 * @param {string} orderId - ID dokumen penjualan.
 * @param {string} newStatus - Status baru.
 */
export const updateSaleStatus = async (orderId, newStatus) => {
  try {
    console.log('🔄 updateSaleStatus: Starting update', { orderId, newStatus });
    
    // Use admin client for development mode to avoid RLS issues
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? (adminSupabase || supabase) : supabase;
    
    const { data, error } = await client
      .from('sales')
      .update({
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .select()
      .single();

    if (error) {
      console.error('❌ updateSaleStatus: Database error:', error);
      throw error;
    }
    
    console.log('✅ updateSaleStatus: Successfully updated', data);
    return data;
  } catch (error) {
    console.error('❌ updateSaleStatus: Error updating sale status:', {
      orderId,
      newStatus,
      error: error.message,
      code: error.code,
      details: error.details
    });
    handleSupabaseError(error);
  }
};

/**
 * Mengupdate status pembayaran penjualan.
 * @param {string} orderId - ID dokumen penjualan.
 * @param {string} newPaymentStatus - Status pembayaran baru.
 */
export const updatePaymentStatus = async (orderId, newPaymentStatus) => {
  try {
    console.log('🔄 updatePaymentStatus: Starting update', { orderId, newPaymentStatus });
    
    // Use admin client for development mode to avoid RLS issues
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? (adminSupabase || supabase) : supabase;
    
    const { data, error } = await client
      .from('sales')
      .update({
        pembayaran: newPaymentStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .select()
      .single();

    if (error) {
      console.error('❌ updatePaymentStatus: Database error:', error);
      throw error;
    }
    
    console.log('✅ updatePaymentStatus: Successfully updated', data);
    return data;
  } catch (error) {
    console.error('❌ updatePaymentStatus: Error updating payment status:', {
      orderId,
      newPaymentStatus,
      error: error.message,
      code: error.code,
      details: error.details
    });
    handleSupabaseError(error);
  }
};

/**
 * Mengupdate dokumen penjualan secara lengkap.
 * @param {string} saleId - ID dokumen penjualan.
 * @param {object} saleData - Data penjualan yang akan diupdate.
 */
export const updateSale = async (saleId, saleData) => {
  try {
    // Use admin client for development mode to avoid RLS issues
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? (adminSupabase || supabase) : supabase;
    
    console.log('🔄 updateSale: Updating sale:', saleId, 'with data:', saleData);

    // Separate items from sale data
    const { items, ...saleUpdateData } = saleData;

    // Update sale record
    const { data: saleResult, error: saleError } = await client
      .from('sales')
      .update({
        ...saleUpdateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', saleId)
      .select()
      .single();

    if (saleError) {
      console.error('❌ updateSale: Error updating sale record:', saleError);
      throw saleError;
    }

    // Update sale items if provided
    if (items && Array.isArray(items) && items.length > 0) {
      console.log('🔄 updateSale: Updating sale items for sale:', saleId);
      
      // Delete existing items
      const { error: deleteError } = await client
        .from('sale_items')
        .delete()
        .eq('sale_id', saleId);

      if (deleteError) {
        console.error('❌ updateSale: Error deleting existing items:', deleteError);
        throw deleteError;
      }

      // Insert updated items dengan validasi kuota diskon
      const saleItems = items.map((item, index) => {
        // Validasi kuota diskon berdasarkan kategori
        const categoryQuota = getCategoryDiscountQuota(item.kategori);
        const validDiscount = Math.min(item.diskon_per_item ?? item.diskonPerItem ?? 0, categoryQuota);
        
        return {
          id: `${saleId}-${index}`,
          sale_id: saleId,
          kode_buku: item.kode_buku,
          judul_buku: item.judul_buku,
          harga_buku: item.harga_buku || 0,
          kuantitas: item.kuantitas || 0,
          diskon_per_item: validDiscount
        };
      });

      const { error: itemsError } = await client
        .from('sale_items')
        .insert(saleItems);

      if (itemsError) {
        console.error('❌ updateSale: Error inserting updated items:', itemsError);
        throw itemsError;
      }
    }

    console.log('✅ updateSale: Successfully updated sale and items');
    return saleResult;
  } catch (error) {
    console.error('❌ updateSale: Final error:', error);
    handleSupabaseError(error);
  }
};

/**
 * Menghapus dokumen penjualan.
 * @param {string} saleId - ID dokumen penjualan yang akan dihapus.
 */
export const deleteSale = async (saleId) => {
  try {
    // Log the saleId being deleted
    console.log('Attempting to delete sale with ID:', saleId);
    
    // Use admin client for delete operations to bypass RLS issues
    // This is needed because DELETE policies are missing from the current schema
    const client = adminSupabase || supabase;
    
    if (!adminSupabase) {
      console.warn('Admin client not available, using regular client. DELETE operations may fail due to RLS policies.');
    }
    
    // First check if the sale exists
    const { data: existingSale, error: checkError } = await client
      .from('sales')
      .select('id, mitra_uid')
      .eq('id', saleId)
      .single();
    
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking sale existence:', checkError);
      throw checkError;
    }
    
    if (!existingSale) {
      throw new Error('Pesanan tidak ditemukan atau Anda tidak memiliki izin untuk menghapusnya');
    }
    
    console.log('Sale exists, proceeding with deletion. Mitra UID:', existingSale.mitra_uid);
    
    // First delete sale items
    const { error: itemsError } = await client
      .from('sale_items')
      .delete()
      .eq('sale_id', saleId);

    if (itemsError) {
      console.error('Error deleting sale items:', itemsError);
      throw itemsError;
    }
    
    console.log('Sale items deleted successfully');

    // Then delete sale
    const { error: saleError } = await client
      .from('sales')
      .delete()
      .eq('id', saleId);

    if (saleError) {
      console.error('Error deleting sale record:', saleError);
      throw saleError;
    }
    
    console.log('Sale deleted successfully');
  } catch (error) {
    console.error('Error deleting sale:', error);
    // Return more specific error messages
    if (error.code === 'PGRST301') {
      throw new Error('Anda tidak memiliki izin untuk menghapus pesanan ini');
    } else if (error.code === 'PGRST116') {
      throw new Error('Pesanan tidak ditemukan');
    } else if (error.code === '42501') {
      throw new Error('Akses ditolak. Anda tidak memiliki izin untuk menghapus pesanan ini');
    }
    throw error;
  }
};

// --- FUNGSI UNTUK BUKU (BOOKS) ---

/**
 * Menambahkan dokumen buku baru.
 * @param {object} bookData - Data lengkap buku.
 */
export const addBook = async (bookData) => {
  try {
    // Use admin client for development mode (same as getAllBooks)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('books')
      .upsert({
        ...bookData,
        created_at: new Date().toISOString()
      }, { onConflict: 'kode_buku' })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding book:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mengambil semua data buku dari database.
 * @returns {Array} Daftar semua buku.
 */
export const getAllBooks = async () => {
  try {
    // Use admin client for development mode
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('books')
      .select('*')
      .order('judul_buku', { ascending: true });

    if (error) {
      console.error('🚨 getAllBooks error:', error);
      throw error;
    }
    
    
    // Return the data with the correct primary key field
    // kode_buku is the primary key in the books table
    return (data || []).map(book => ({
      ...book,
      id: book.kode_buku // Keep for compatibility, but use kode_buku as the actual identifier
    }));
  } catch (error) {
    console.error('❌ Error getting books:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Mengimpor beberapa buku sekaligus menggunakan batch insert.
 * @param {Array<object>} books - Array berisi objek-objek buku.
 */
export const importBooksBatch = async (books) => {
  try {
    const booksWithTimestamp = books
      .filter(book => book.kode_buku)
      .map(book => ({
        ...book,
        created_at: new Date().toISOString()
      }));

    // Use admin client for development mode (same as getAllBooks)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;

    const { data, error } = await client
      .from('books')
      .upsert(booksWithTimestamp, { onConflict: 'kode_buku' });

    if (error) throw error;
    console.log('Books imported successfully');
    return data;
  } catch (error) {
    console.error('Error importing books:', error);
    handleSupabaseError(error);
  }
};

/**
 * Memperbarui buku di Supabase.
 * @param {string} bookId - Kode buku yang akan diperbarui.
 * @param {object} updateData - Data yang akan diperbarui.
 */
export const updateBook = async (bookId, updateData) => {
  try {
    console.log('🔧 Updating book:', bookId, 'with data:', updateData);
    
    // Use admin client for development mode (same as getAllBooks)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('books')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('kode_buku', bookId)
      .select();

    if (error) {
      console.error('🚨 Update book error:', error);
      throw error;
    }
    
    console.log('✅ Book update result:', data);
    
    if (!data || data.length === 0) {
      console.warn('⚠️ No rows were updated for book:', bookId);
      throw new Error(`No book found with kode_buku: ${bookId}`);
    }
    
    
    // Return the first item if array, or the data itself
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ Error updating book:', error);
    handleSupabaseError(error);
  }
};

/**
 * Menghapus buku dari Supabase.
 * @param {string} bookId - Kode buku yang akan dihapus.
 */
export const deleteBook = async (bookId) => {
  try {
    // Use admin client for development mode (same as getAllBooks)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { error } = await client
      .from('books')
      .delete()
      .eq('kode_buku', bookId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting book:', error);
    handleSupabaseError(error);
  }
};

// --- FUNGSI UNTUK PELANGGAN (CUSTOMERS) ---

/**
 * Menambahkan pelanggan baru.
 * @param {object} customerData - Data lengkap pelanggan.
 */
export const addCustomer = async (customerData) => {
  try {
    // Use admin client for development mode (same consistency as other functions)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    console.log('Adding customer:', customerData);
    
    const { data, error } = await client
      .from('customers')
      .insert({
        mitra_uid: customerData.mitraUid,
        nama: customerData.nama_pelanggan,
        no_wa: customerData.no_wa,
        alamat: customerData.alamat,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    console.log('Customer added with ID:', data.id);
    return data;
  } catch (error) {
    console.error('Error in addCustomer:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mengambil data pelanggan berdasarkan mitra.
 * @param {string} mitraUid - UID mitra.
 * @returns {Array} Daftar pelanggan dari mitra tertentu.
 */
export const getCustomersByMitra = async (mitraUid) => {
  if (!mitraUid) {
    console.error('getCustomersByMitra: mitraUid is required');
    return [];
  }
  
  try {
    // Use admin client for development mode (same consistency as other functions)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('customers')
      .select('*')
      .eq('mitra_uid', mitraUid)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    console.log('Found customers:', (data || []).length);
    return data || [];
  } catch (error) {
    console.error('Error in getCustomersByMitra:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mengambil semua pelanggan dari semua mitra (untuk admin).
 * @returns {Array} Daftar semua pelanggan.
 */
export const getAllCustomers = async () => {
  try {
    // Use admin client for development mode
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('customers')
      .select(`
        *,
        mitra:app_users!mitra_uid (
          nama,
          kode_mitra
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('🚨 getAllCustomers error:', error);
      throw error;
    }
    
    
    // Transform data to match expected format
    return (data || []).map(customer => ({
      ...customer,
      // Transform field names to match expected format
      nama_pelanggan: customer.nama,
      // Get mitra name from joined data, fallback to kode_mitra if nama is null
      nama_mitra: customer.mitra?.nama || customer.mitra?.kode_mitra || 'Unknown Mitra'
    }));
  } catch (error) {
    console.error('❌ Error getting all customers:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Mengupdate data pelanggan.
 * @param {string} customerId - ID pelanggan.
 * @param {object} customerData - Data pelanggan yang akan diupdate.
 */
export const updateCustomer = async (customerId, customerData) => {
  try {
    console.log('🔄 updateCustomer: Starting update for customer ID:', customerId);
    console.log('🔄 updateCustomer: Customer data:', customerData);
    
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    
    if (!customerData) {
      throw new Error('Customer data is required');
    }
    
    // Use admin client for development mode (same consistency as other functions)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    // Prepare update data, ensuring we handle the field name mapping correctly
    const updateData = {
      nama: customerData.nama_pelanggan || customerData.nama,
      no_wa: customerData.no_wa,
      alamat: customerData.alamat
    };
    
    console.log('🔄 updateCustomer: Update data prepared:', updateData);
    
    const { data, error } = await client
      .from('customers')
      .update(updateData)
      .eq('id', customerId)
      .select()
      .single();

    if (error) {
      console.error('❌ updateCustomer: Supabase error:', error);
      throw error;
    }
    
    console.log('✅ updateCustomer: Customer updated successfully:', data);
    return data;
  } catch (error) {
    console.error('❌ updateCustomer: Error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint,
      customerId,
      customerData
    });
    
    // Provide more specific error messages
    let errorMessage = error.message || 'Gagal memperbarui data pelanggan';
    
    if (error.code === 'PGRST116') {
      errorMessage = 'Pelanggan tidak ditemukan';
    } else if (error.code === '42501') {
      errorMessage = 'Anda tidak memiliki izin untuk memperbarui data pelanggan ini';
    } else if (error.message?.includes('unique')) {
      errorMessage = 'Data pelanggan sudah ada';
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Menghapus pelanggan.
 * @param {string} customerId - ID pelanggan yang akan dihapus.
 */
export const deleteCustomer = async (customerId) => {
  try {
    console.log('Deleting customer:', customerId);
    // Use admin client for development mode (same consistency as other functions)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { error } = await client
      .from('customers')
      .delete()
      .eq('id', customerId);

    if (error) throw error;
    console.log('Customer deleted successfully');
  } catch (error) {
    console.error('Error in deleteCustomer:', error);
    handleSupabaseError(error);
  }
};

// --- FUNGSI UNTUK KATEGORI BUKU ---

/**
 * Menambahkan kategori buku baru ke Supabase.
 * @param {object} categoryData - Data kategori yang akan ditambahkan.
 */
export const addCategory = async (categoryData) => {
  try {
    // Use admin client for development mode to allow inserts with RLS
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    const payload = {
      nama_kategori: categoryData.nama_kategori,
      deskripsi: categoryData.deskripsi ?? null,
      kuota_diskon: typeof categoryData.kuota_diskon === 'number' ? categoryData.kuota_diskon : Number(categoryData.kuota_diskon) || 0,
      created_at: new Date().toISOString()
    };

    // First attempt with kuota_diskon/deskripsi (if columns exist)
    let { data, error } = await client
      .from('categories')
      .insert(payload)
      .select()
      .single();

    // If optional column(s) don't exist, retry with minimal payload (backward-compat)
    if (error && isMissingColumnError(error)) {
      const fallbackPayload = { nama_kategori: payload.nama_kategori, created_at: payload.created_at };
      ({ data, error } = await client
        .from('categories')
        .insert(fallbackPayload)
        .select()
        .single());
    }

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding category:', error);
    handleSupabaseError(error);
  }
};

/**
 * Mengambil semua kategori buku dari Supabase.
 * @returns {Array} Daftar semua kategori buku.
 */
export const getAllCategories = async () => {
  try {
    // Use admin client for development mode
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    
    const { data, error } = await client
      .from('categories')
      .select('*')
      .order('nama_kategori', { ascending: true });

    if (error) {
      console.error('🚨 getAllCategories error:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('❌ Error getting categories:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Memperbarui kategori buku di Supabase.
 * @param {string} categoryId - ID kategori yang akan diperbarui.
 * @param {object} updateData - Data yang akan diperbarui.
 */
export const updateCategory = async (categoryId, updateData) => {
  try {
    // Use admin client for development mode to bypass RLS during updates
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    const payload = {
      nama_kategori: updateData.nama_kategori,
      deskripsi: updateData.deskripsi ?? null,
      // only include kuota_diskon if it's a valid number
      ...(updateData.kuota_diskon !== undefined ? { kuota_diskon: Number(updateData.kuota_diskon) || 0 } : {}),
      updated_at: new Date().toISOString()
    };

    let { data, error } = await client
      .from('categories')
      .update(payload)
      .eq('id', categoryId)
      .select()
      .single();

    if (error && isMissingColumnError(error)) {
      const fallbackPayload = { nama_kategori: payload.nama_kategori, updated_at: payload.updated_at };
      ({ data, error } = await client
        .from('categories')
        .update(fallbackPayload)
        .eq('id', categoryId)
        .select()
        .single());
    }

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating category:', error);
    handleSupabaseError(error);
  }
};

/**
 * Menghapus kategori buku dari Supabase.
 * @param {string} categoryId - ID kategori yang akan dihapus.
 */
export const deleteCategory = async (categoryId) => {
  try {
    // Use admin client for development mode to bypass RLS during deletes
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    const { error } = await client
      .from('categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting category:', error);
    handleSupabaseError(error);
  }
};

// --- FUNGSI FILE UPLOAD (STORAGE) ---
// Note: Supabase Storage functions for file uploads
// This replaces Firebase Storage functionality

/**
 * Upload file ke Supabase Storage
 * @param {File} file - File yang akan diupload
 * @param {string} bucket - Bucket name (resi/bukti-bayar)
 * @param {string} orderNumber - Nomor pesanan untuk nama file
 * @returns {Promise<string>} URL public file
 */
export const uploadFile = async (file, bucket, orderNumber) => {
  try {
    console.log('📁 uploadFile: Starting upload to bucket:', bucket);
    
    // Validasi ukuran file (max 1MB)
    if (file.size > 1024 * 1024) {
      throw new Error('Ukuran file maksimal 1MB');
    }

    // Validasi tipe file
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipe file harus JPG, JPEG, atau PNG');
    }

    // Use admin client for development mode to bypass RLS issues, fallback to regular client
    const client = (process.env.REACT_APP_DEV_MODE === 'true' && adminSupabase) ? adminSupabase : supabase;

    // Generate nama file unik
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${orderNumber}-${timestamp}.${fileExtension}`;
    
    console.log('📁 Uploading file:', fileName, 'to bucket:', bucket);
    
    // Upload file
    const { data, error } = await client.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ Upload error:', error);
      throw error;
    }


    // Get public URL using admin client
    const { data: publicData } = client.storage
      .from(bucket)
      .getPublicUrl(data.path);
    
    console.log('🔗 Generated public URL:', publicData.publicUrl);
    
    return publicData.publicUrl;
  } catch (error) {
    console.error('❌ Error uploading file:', error);
    
    // Provide more specific error messages
    if (error.message.includes('Bucket not found')) {
      throw new Error(`Storage bucket '${bucket}' tidak ditemukan. Silakan hubungi administrator.`);
    }
    
    handleSupabaseError(error);
  }
};

/**
 * Hapus file dari Supabase Storage
 * @param {string} fileURL - URL file yang akan dihapus
 * @param {string} bucket - Bucket name
 */
export const deleteFile = async (fileURL, bucket) => {
  try {
    if (!fileURL) return;
    
    // Extract filename dari URL
    const url = new URL(fileURL);
    const path = url.pathname.split('/').pop();
    
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting file:', error);
    handleSupabaseError(error);
  }
};

/**
 * Update pesanan dengan file upload (resi/bukti bayar)
 * @param {string} saleId - ID pesanan
 * @param {object} uploadData - Data upload {resi_number?, bukti_pembayaran_url?}
 */
export const updateSaleWithFiles = async (saleId, uploadData) => {
  try {
    // Use admin client for development mode (same consistency as other functions)
    const client = process.env.REACT_APP_DEV_MODE === 'true' ? adminSupabase : supabase;
    console.log('Updating sale:', saleId, 'with upload data:', uploadData);
    
    const { data, error } = await client
      .from('sales')
      .update({
        ...uploadData,
        updated_at: new Date().toISOString()
      })
      .eq('id', saleId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('❌ Error updating sale with files:', error);
    handleSupabaseError(error);
  }
};

/**
 * Upload bukti pembayaran untuk pesanan
 * @param {string} saleId - ID pesanan
 * @param {File} file - File bukti pembayaran
 * @param {string} orderNumber - Nomor pesanan
 * @returns {Promise<object>} Data pesanan yang diupdate
 */
export const uploadBuktiPembayaran = async (saleId, file, orderNumber) => {
  try {
    console.log('📄 Uploading bukti pembayaran for sale:', saleId);
    
    // Upload file ke storage
    const fileUrl = await uploadFile(file, 'bukti-pembayaran', orderNumber);
    
    // Update sale dengan URL bukti pembayaran
    const updatedSale = await updateSaleWithFiles(saleId, {
      bukti_pembayaran_url: fileUrl
    });
    
    console.log('✅ Bukti pembayaran uploaded successfully');
    return updatedSale;
  } catch (error) {
    console.error('❌ Error uploading bukti pembayaran:', error);
    throw error;
  }
};

// Legacy function compatibility
export const updateOldOrdersWithOrderNumbers = async () => {
  console.log('updateOldOrdersWithOrderNumbers: Not needed for Supabase migration');
  return 0;
};

export const updateOldOrdersWithOrderNumber = async () => {
  console.log('updateOldOrdersWithOrderNumber: Not needed for Supabase migration');
  return 0;
};

export const updateLegacyOrdersWithOrderNumber = async () => {
  console.log('updateLegacyOrdersWithOrderNumber: Not needed for Supabase migration');
  return 0;
};
